// A cultivated farm plot, described the way age-of-mcp authors it: local helper
// functions and loops lay out the farm-specific soil, plough ridges, crop rows
// and scarecrow, while the shared yard props (fence, garden patch, barrel) come
// from the library — the same split age-of-mcp's farm.ts uses (local field
// generators + imported props). There is no terrain in the model library, so
// the plot is flat (y = 0 is the floor) rather than warped to a height grid.
import * as THREE from "three";
import { defineModel, pace, fenceRect, gardenPatch, barrel, type Part } from "../../lib";

const SOIL = "#6b4a2c";    // tilled earth
const RIDGE = "#5a3d22";   // raised plough ridge
const SPROUT = "#5f9a43";  // young green crop
const RIPE = "#caa53a";    // ripe golden crop
const POST = "#7a5a30";    // scarecrow post / arm
const CLOTH = "#b8593a";   // scarecrow head cloth

// The field is FOOTPRINT-NATIVE (3x3): authored at its true size, centred on the
// origin. It reaches +/-1.46 tiles, with crop/ridge rows laid across the plot.
const HALF = 1.46;
const ROWS = [-1.2, -0.8, -0.4, 0, 0.4, 0.8, 1.2];   // crop/ridge rows across z
const COLS = [-1.2, -0.8, -0.4, 0, 0.4, 0.8, 1.2];   // plants across x

// A tilled-soil slab covering the whole footprint.
function soil(): Part {
  return { geometry: new THREE.BoxGeometry(HALF * 2, 0.12, HALF * 2), color: SOIL, position: [0, -0.06, 0] };
}

// Raised plough ridges running across the plot.
function rows(): Part[] {
  return ROWS.map((z): Part => ({
    geometry: new THREE.BoxGeometry(HALF * 1.9, 0.045, 0.1),
    color: RIDGE,
    position: [0, 0.06, z],
  }));
}

// Rows of crop tufts — green sprouts or ripe gold — standing on the soil.
function crops(color: string, h: number): Part[] {
  const out: Part[] = [];
  for (const z of ROWS) {
    for (const x of COLS) {
      out.push({ geometry: new THREE.ConeGeometry(0.05, h, 5), color, position: [x, 0.04 + h / 2, z] });
    }
  }
  return out;
}

// A scarecrow standing in a corner of a ripe field.
function scarecrow(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.03, 0.03, 0.6, 5), color: POST, position: [-1.1, 0.32, -1.1] },
    { geometry: new THREE.BoxGeometry(0.4, 0.05, 0.05), color: POST, position: [-1.1, 0.5, -1.1] },
    { geometry: new THREE.BoxGeometry(0.13, 0.13, 0.13), color: CLOTH, position: [-1.1, 0.62, -1.1] },
  ];
}

// A cultivated field that grows from bare tilled soil, through plough ridges and
// green sprouts, to a ripe golden crop with a fence, garden, barrel and
// scarecrow — the four build stages age-of-mcp's farm walks through.
export const farm = defineModel({
  id: "farm",
  tiles: { w: 3, h: 3 },
  batchId: "standard",
  name: "Farm",
  tags: ["building", "farm", "low-poly", "age-of-mcp", "classic"],
  summary: "Low-poly farm.",
  description: "A cultivated plot that grows a renewable food crop.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Tilled soil", parts: () => [soil()] },
    { id: "stage-1", label: "Ploughed rows", parts: () => [soil(), ...rows()] },
    { id: "stage-2", label: "Sprouting", parts: () => [soil(), ...rows(), ...crops(SPROUT, 0.16)] },
    {
      id: "complete",
      label: "Complete",
      parts: () => [
        soil(),
        ...rows(),
        ...crops(RIPE, 0.3),
        ...scarecrow(),
        ...fenceRect(-1.42, -1.42, 1.42, 1.42, 0.05),
        ...gardenPatch(1.0, 1.0, 0.05),
        barrel(-1.1, 1.05, 0.05),
      ],
    },
  ],
  animations: [],
  stations: [
    { id: "field", position: [0, 0, 0], facing: 0, capacity: 6, description: "The crop rows a farmer paces." },
  ],
  interactions: [
    { id: "tend", label: "Tending the crop", station: "field", variants: ["complete"], ...pace([[-1.08, -1.08], [1.08, -1.08], [1.08, 0], [-1.08, 0], [-1.08, 1.08], [1.08, 1.08]]) },
  ],
});
